import t from 'react-translate';
import type { ValidationResult } from '../types';
import { MAX_FILE_SIZE, MAX_NUMBER_OF_FILES, ACCEPTED_FILE_EXTENSIONS } from '../constants';
import { getFileExtension } from './file-helpers';

const isAcceptedExtension = (extension: string): boolean => ACCEPTED_FILE_EXTENSIONS.some((acceptedExt) => acceptedExt === extension);

export const validateFileType = (file: File): ValidationResult => {
  const extension = getFileExtension(file.name);
  const isValid = isAcceptedExtension(extension);

  return {
    isValid,
    errors: isValid ? [] : [t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE()],
  };
};

export const validateFileSize = (file: File): ValidationResult => {
  const isValid = file.size <= MAX_FILE_SIZE;

  return {
    isValid,
    errors: isValid ? [] : [t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.FILE_IS_TOO_LARGE()],
  };
};

export const validateFilesCount = (currentCount: number, addingCount: number): ValidationResult => {
  const totalCount = currentCount + addingCount;
  const isValid = totalCount <= MAX_NUMBER_OF_FILES;

  return {
    isValid,
    errors: isValid ? [] : [t.LECTURE_PAGES.LHS.NOVO_AI.ERROR_MSG.FILES_LIMIT_ERROR(MAX_NUMBER_OF_FILES)],
  };
};

export const validateSingleFile = (file: File): ValidationResult => {
  const typeValidation = validateFileType(file);
  const sizeValidation = validateFileSize(file);

  const allErrors = [...typeValidation.errors, ...sizeValidation.errors];

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
};

export const validateFiles = (files: File[], currentCount: number): ValidationResult => {
  // First, check if adding these files would exceed the maximum allowed count
  const countValidation = validateFilesCount(currentCount, files.length);
  if (!countValidation.isValid) {
    return countValidation;
  }

  // Collect files that fail validation, grouped by error type
  const invalidTypeFiles: string[] = [];
  const oversizedFiles: string[] = [];

  files.forEach((file) => {
    const typeValidation = validateFileType(file);
    const sizeValidation = validateFileSize(file);

    if (!typeValidation.isValid) {
      invalidTypeFiles.push(file.name);
    }
    if (!sizeValidation.isValid) {
      oversizedFiles.push(file.name);
    }
  });

  // Build consolidated error messages based on validation results
  const errors: string[] = [];

  // Add type validation error
  if (invalidTypeFiles.length > 0) {
    errors.push(t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.MULTIPLE_INVALID_FILE_TYPE());
  }

  // Add size validation error
  if (oversizedFiles.length > 0) {
    errors.push(t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADDITIONAL_RESOURCES.FILE_IS_TOO_LARGE());
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

